import { action, computed, makeObservable, observable, onBecomeObserved, runInAction } from "mobx";
import { AppSubStore, AppSubStoreArgs } from "@/store/types";
import { TemplateObservable } from "@/store/templates/TemplateObservable";
import { DeleteTemplateModalStore } from "@/components/modal/delete-template/delete-template-modal-store";
import { ShareSheetModalStore } from "@/components/modal/share-sheet/share-sheet-modal-store";
import { AddToCollectionModalStore } from "@/components/modal/add-to-collection/add-to-collection-modal-store";
import { FileUploadRejectedModalStore } from "@/components/modal/file-upload-rejected/file-upload-rejeted-modal-store";
import { PdfPageLimitWarningModalStore } from "@/components/modal/pdf-page-limit-warning/pdf-page-limit-warning-modal-store";
import { INotesViewPageStore } from "@/store/pages/NotesViewPageStore/types";
import { uuidModule } from "@/modules/uuid";
import { LensKind, SortByKind } from "@/modules/lenses/types";
import { liveQuery, Subscription } from "dexie";
import { TemplateIndexTuple } from "@/store/templates/types";
import { Maybe } from "@/domains/common/types";

export type SettingsTemplatesSubView = "list" | "edit";

export type SettingsTemplateBannerState =
  | {
      kind: "sharing";
      privateCollectionNames: string[];
      sharedNames: string[];
      isAutoSharingEnabled: boolean;
    }
  | {
      kind: "collections";
      collectionNames: string[];
      isAutoSharingEnabled: boolean;
    };

export class SettingsModalTemplatesPageStore extends AppSubStore {
  subView: SettingsTemplatesSubView = "list";
  activeTemplateId: string | null = null;

  myTemplatesTuples: TemplateIndexTuple[] = [];
  calendarTemplatesTuples: TemplateIndexTuple[] = [];
  sharedWithMeTemplatesTuples: TemplateIndexTuple[] = [];

  private myTemplatesSubscription: Maybe<Subscription> = undefined;
  private calendarTemplatesSubscription: Maybe<Subscription> = undefined;
  private sharedWithMeSubscription: Maybe<Subscription> = undefined;
  private liveQueriesInitialized = false;

  deleteTemplateModal: DeleteTemplateModalStore;
  shareSheetModal: ShareSheetModalStore;
  addToCollectionModal: AddToCollectionModalStore;
  fileUploadRejectedModal: FileUploadRejectedModalStore;
  pdfPageLimitWarningModal: PdfPageLimitWarningModalStore;

  constructor(injectedDeps: AppSubStoreArgs) {
    super(injectedDeps);

    this.deleteTemplateModal = new DeleteTemplateModalStore(injectedDeps, {
      onClose: () => this.navigateToTemplateList(),
    });
    this.shareSheetModal = ShareSheetModalStore.forAppStore({ appStore: this.store });
    this.addToCollectionModal = new AddToCollectionModalStore(injectedDeps, {});
    this.fileUploadRejectedModal = new FileUploadRejectedModalStore(injectedDeps);
    this.pdfPageLimitWarningModal = new PdfPageLimitWarningModalStore();

    makeObservable<
      this,
      | "myTemplatesSubscription"
      | "calendarTemplatesSubscription"
      | "sharedWithMeSubscription"
      | "liveQueriesInitialized"
    >(this, {
      subView: observable,
      activeTemplateId: observable,
      myTemplatesTuples: observable,
      calendarTemplatesTuples: observable,
      sharedWithMeTemplatesTuples: observable,
      myTemplatesSubscription: false,
      calendarTemplatesSubscription: false,
      sharedWithMeSubscription: false,
      liveQueriesInitialized: false,
      deleteTemplateModal: false,
      shareSheetModal: false,
      addToCollectionModal: false,
      fileUploadRejectedModal: false,
      pdfPageLimitWarningModal: false,
      openFileUploadRejectedModal: true,
      openPdfPageLimitWarningModal: true,
      navigateToTemplateEdit: action,
      navigateToTemplateList: action,
      createTemplate: action,
      myTemplates: computed,
      calendarTemplates: computed,
      sharedWithMeTemplates: computed,
      activeTemplate: computed,
      activeTemplateBanner: computed,
      initializeLiveQueries: action,
    });

    onBecomeObserved(this, "myTemplatesTuples", () => this.initializeLiveQueries());
    onBecomeObserved(this, "calendarTemplatesTuples", () => this.initializeLiveQueries());
    onBecomeObserved(this, "sharedWithMeTemplatesTuples", () => this.initializeLiveQueries());
  }

  get myTemplates(): TemplateObservable[] {
    return this.myTemplatesTuples
      .map(tuple => this.store.templates.get(tuple.at(-1) as string))
      .filter((t): t is TemplateObservable => t !== undefined);
  }

  get calendarTemplates(): TemplateObservable[] {
    return this.calendarTemplatesTuples
      .map(tuple => this.store.templates.get(tuple.at(-1) as string))
      .filter((t): t is TemplateObservable => t !== undefined);
  }

  get sharedWithMeTemplates(): TemplateObservable[] {
    return this.sharedWithMeTemplatesTuples
      .map(tuple => this.store.templates.get(tuple.at(-1) as string))
      .filter((t): t is TemplateObservable => t !== undefined);
  }

  get activeTemplate(): TemplateObservable | undefined {
    if (!this.activeTemplateId) return undefined;
    return this.store.templates.get(this.activeTemplateId);
  }

  /**
   * Banner logic for the edit view:
   * - hasTargetScopes → sharing banner (copy varies by private collections / shared names)
   * - no target scopes but has collections → collections banner
   * - no target scopes and no collections → no banner
   */
  get activeTemplateBanner(): SettingsTemplateBannerState | null {
    const template = this.activeTemplate;
    if (!template) return null;

    const targetScopes = template.modelData.target_scopes;
    const targetScopeCollectionIds = targetScopes
      .filter(scope => scope.scope_kind === "COLLECTION_SCOPE")
      .map(scope => scope.value.collection_id);
    const targetScopeSpaceAccountIds = targetScopes
      .filter(scope => scope.scope_kind === "SPACE_ACCOUNT_SCOPE")
      .map(scope => scope.value.space_account_id)
      .filter(id => id !== this.store.spaceAccounts.myPersonalSpaceAccountId);

    const targetScopeCollections = this.store.collections.getMany(targetScopeCollectionIds);
    const targetScopeContacts = this.store.contacts.getManyBySpaceAccountId(
      targetScopeSpaceAccountIds
    );
    const hasTargetScopes = targetScopeCollections.length > 0 || targetScopeContacts.length > 0;

    if (hasTargetScopes) {
      const privateCollectionNames = targetScopeCollections
        .filter(c => c.modelData.shared_at === null)
        .map(c => c.label);
      const sharedCollectionNames = targetScopeCollections
        .filter(c => c.modelData.shared_at !== null)
        .map(c => c.label);
      const contactEmails = targetScopeContacts
        .map(c => c?.profileEmailAddress)
        .filter(Boolean) as string[];
      const sharedNames = [...sharedCollectionNames, ...contactEmails];

      return {
        kind: "sharing",
        privateCollectionNames,
        sharedNames,
        isAutoSharingEnabled: template.isAutoSharingEnabled,
      };
    }

    const collections = template.collectionList?.allCollections || [];
    if (collections.length > 0) {
      return {
        kind: "collections",
        collectionNames: collections.map(c => c.label),
        isAutoSharingEnabled: template.isAutoSharingEnabled,
      };
    }

    return null;
  }

  openFileUploadRejectedModal: INotesViewPageStore["openFileUploadRejectedModal"] = ({
    code,
    fileName,
  }) => {
    this.fileUploadRejectedModal.open({ code, fileName });
  };

  openPdfPageLimitWarningModal: INotesViewPageStore["openPdfPageLimitWarningModal"] = ({
    pageCount,
  }) => {
    return this.pdfPageLimitWarningModal.open({ pageCount });
  };

  navigateToTemplateEdit(templateId: string) {
    this.subView = "edit";
    this.activeTemplateId = templateId;
  }

  navigateToTemplateList() {
    this.subView = "list";
    this.activeTemplateId = null;
  }

  async createTemplate() {
    const templateId = uuidModule.generate();
    await this.store.templates.createTemplate({ templateId });
    runInAction(() => {
      this.navigateToTemplateEdit(templateId);
    });
  }

  initializeLiveQueries() {
    if (this.liveQueriesInitialized) return;
    this.liveQueriesInitialized = true;

    // My templates: owned by me, excluding default calendar event template
    this.myTemplatesSubscription?.unsubscribe();
    this.myTemplatesSubscription = liveQuery(() =>
      this.store.templates.search({
        lens: LensKind.AddedByMe,
        sortBy: SortByKind.LastModified,
        filter: { isDefaultCalendarEventTemplate: false },
      })
    ).subscribe({
      next: rows =>
        runInAction(() => {
          this.myTemplatesTuples = rows;
        }),
    });

    // Calendar and meetings: default calendar event templates
    this.calendarTemplatesSubscription?.unsubscribe();
    this.calendarTemplatesSubscription = liveQuery(() =>
      this.store.templates.search({
        lens: LensKind.All,
        sortBy: SortByKind.LastModified,
        filter: { isDefaultCalendarEventTemplate: true },
      })
    ).subscribe({
      next: rows =>
        runInAction(() => {
          this.calendarTemplatesTuples = rows;
        }),
    });

    // Shared with me: shared templates, excluding default calendar event template
    this.sharedWithMeSubscription?.unsubscribe();
    this.sharedWithMeSubscription = liveQuery(() =>
      this.store.templates.search({
        lens: LensKind.SharedWithMe,
        sortBy: SortByKind.LastModified,
        filter: { isDefaultCalendarEventTemplate: false },
      })
    ).subscribe({
      next: rows =>
        runInAction(() => {
          this.sharedWithMeTemplatesTuples = rows;
        }),
    });
  }
}
